// src/hooks/useUserTemplate.js
// useUserTemplate to only initialize dynamic formData, reusing the static template.
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";

export const useUserTemplate = (
  mainId,
  userId,
  companyId,
  setFormData,
  formData,
  templateData,
  reset,
) => {
  // ✅ helper: deep merge two objects safely
  const deepMerge = (target = {}, source = {}) => {
    const output = { ...target };
    for (const key of Object.keys(source)) {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        output[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    }
    return output;
  };

  useEffect(() => {
    if (!templateData || !mainId || !userId || !companyId) return;

    const { buyerSections } = templateData;

    // Everything below reads `prevFormData` (the functional updater's own
    // parameter) instead of the `formData` closed over from this hook's
    // props. That closure reflects whatever `formData` was on the render
    // that last ran this effect — not necessarily the true latest state if
    // another update (e.g. ManualSheetSection's Origination Fee % -> $
    // conversion) is still pending/batched at the moment this effect fires.
    // `setFormData(someObject)` (a direct replacement, not `setFormData(prev
    // => ...)`) would silently overwrite that pending update with a stale
    // snapshot — which is exactly what was happening here.
    let computedFormData;
    setFormData((prevFormData) => {
      const titleSettlementMeta = prevFormData?.titleSettlementMeta || {
        ownersTitleAmount: "0.00",
        ownersTitleBuyer: "0.00",
        ownersTitlePayor: "Seller",
        ownersPolicy: true,
        lendersPolicy: true,
        escrowSettlement: true,
        escrowSettlementPayor: "Seller",
        escrowSettlementAmount: "0.00",
        escrowSettlementBuyer: "0.00",
        escrowSettlementSplit: "0.00",
        lendersTitleAmount: "0.00",
        lendersTitleBuyer: "0.00",
        lendersTitlePayor: "Seller",
        discount: "0.00",
        discountAmount: "0.00",
      };

      const addressMeta = {
        address: "",
        unitNumber: "",
        street_no: "",
        street_name: "",
        city: "",
        state: "",
        county: "",
        zip: "",
        closing_state: "",
        closing_county: "",
        closing_zip_code: "",
      };

      const finalFormData = {
        addressMeta,
        addressVerified: false,
        preparedBy: prevFormData?.preparedBy ?? "",
        preparedFor: prevFormData?.preparedFor ?? "New Client",
        salesPrice: "0.00",
        escrowPremiumChanged: false,
        loanInfoMeta: {
          estimatedClosingDate: "",
          buyerDownPayment: "0.00",
          requestedDownPayment: "0.00",
          buyersLoanType: "Conventional",
          annualPropertyTax: "0.00",
          annualPropertyTaxAmount: "0.00",
          assessorsTaxRate: "0.00",
          autoEstimateLoan: false,
          conventionalPercent: "80.00",
          conventionalPercent2: "0.00",
          interestRate1:
            buyerSections.tenure.find(
              (it) => it.key === "todays_interest_rate",
            )?.value ?? "0.00",
          interestRate2: "0.00",
          termsInYears1:
            buyerSections.tenure.find(
              (it) => it.key === "terms_of_loans_in_years",
            )?.value ?? "0",
          termsInYears2: "0",
          amount: "0.00",
          amount2: "0.00",
          adjusted: "0.00",
          noPMICheck: false,
          showAPRCheck: false,
          allowOwnerCarry1Check: false,
          allowOwnerCarry2Check: false,
          addDueYear1: "0.00",
          addDueYear2: "0.00",
        },
        titleSettlementMeta,
        closingCostsMeta: buyerSections.closingCosts,
        buyerClosingCostsMeta: buyerSections.conventional,
        compensationMeta: {
          sellerBrokerCompensation: "1.00",
          sellerBrokerCompensationAmount: "0.00",
          buyerBrokerCompensation: "2.00",
          buyerBrokerCompensationAmount: "0.00",
          totalAgtAmount: "0.00",
        },
        prepaidsMeta: {
          monthsTaxes: "0",
          rate:
            buyerSections.insurance.find(
              (it) => it.key === "tax_rate_per_year_per_100_of_sale_price",
            )?.value ?? "0.00",
          taxesAmount: "0.00",
          monthsIns:
            buyerSections.insurance.find(
              (it) => it.key === "number_of_months_insurance_prepaid",
            )?.value ?? "0.00",
          rateThou:
            buyerSections.insurance.find(
              (it) =>
                it.key ===
                "homeowner_insurance_rate_per_year_per_1000_of_sale_price",
            )?.value ?? "0.00",
          insAmount: "0.00",
          daysInterest:
            buyerSections.tenure.find(
              (it) => it.key === "number_of_days_per_month",
            )?.value ?? "0.00",
          daysInterestAmount: "0.00",
          twoMonthsPMI: "0.00",
          fhaMip: "0.00",
          fhaMipCheck: true,
          fhaMipAmount: "0.00",
          FhaMipFin: "0.00",
          FhaMipFin2: "0.00",
          vaffFinance: "0.00",
          vaffFinanceCheck: true,
          vaffFinanceRate: "0.00",
          vaffFinanceAmount: "0.00",
          mipFinance: "0.00",
          mipFinanceCheck: true,
          mipFinanceAmount: "0.00",
          costs:
            buyerSections.prepaid.find((it) => it.key === "prepaid_other")
              ?.label ?? "Other",
          costsAmount:
            buyerSections.prepaid.find((it) => it.key === "prepaid_other")
              ?.value ?? "Other",
          totalPrepaidItems: "0.00",
        },
        paymentMeta: {
          principalAndInterest: "0.00",
          realEstateTaxes: "0.00",
          homeownerInsurance: "0.00",
          rateMonthlyPMI: "0.00",
          rateMonthlyPMIAmount: "0.00",
          pAndI2ndTDAmount: "0.00",
          monthlyExpense1:
            buyerSections.payments.find((it) => it.key === "other_4")
              ?.label ?? "Other",
          monthlyExpense2:
            buyerSections.payments.find((it) => it.key === "hoa_4")?.label ??
            "Other",
          monthlyExpense1Amount:
            buyerSections.payments.find((it) => it.key === "other_4")
              ?.value ?? "0.00",
          monthlyExpense2Amount:
            buyerSections.payments.find((it) => it.key === "hoa_4")?.value ??
            "0.00",
          totalMonthlyPayment: "0.00",
          estimatedTaxProrations: "0.00",
        },
        totalClosingMeta: {
          totalInvestment: "0.00",
          totalCommission: "0.00",
          totalClosingCosts: "0.00",
          totalPrepaids: "0.00",
          totalMonthlyPayment: "0.00",
        },
      };

      const mergedFormData = deepMerge(finalFormData, prevFormData);
      // Same reasoning as annualPropertyTax below: this reload can fire again
      // for the same company (it's not gated to genuine company switches),
      // and unconditionally replacing these with the fresh template array
      // would discard any in-progress work already done on them — including,
      // notably, ManualSheetSection's own Origination Fee % -> $ conversion,
      // which this reload firing again used to silently undo, in a race the
      // conversion effect could never win.
      const hasExistingClosingCosts =
        Array.isArray(prevFormData?.closingCostsMeta) &&
        prevFormData.closingCostsMeta.length > 0;
      const hasExistingBuyerClosingCosts =
        Array.isArray(prevFormData?.buyerClosingCostsMeta) &&
        prevFormData.buyerClosingCostsMeta.length > 0;
      const withFreshCompanyTemplate = {
        ...mergedFormData,
        closingCostsMeta: hasExistingClosingCosts
          ? prevFormData.closingCostsMeta
          : finalFormData.closingCostsMeta,
        buyerClosingCostsMeta: hasExistingBuyerClosingCosts
          ? prevFormData.buyerClosingCostsMeta
          : finalFormData.buyerClosingCostsMeta,
        loanInfoMeta: {
          ...mergedFormData.loanInfoMeta,
          interestRate1: finalFormData.loanInfoMeta.interestRate1,
          termsInYears1: finalFormData.loanInfoMeta.termsInYears1,
          // A company-template reload can be triggered mid-flow (e.g. the address
          // just entered resolves a different title company than the session
          // started with). That must never blow away a property tax amount the
          // user already confirmed for the current address.
          annualPropertyTax:
            prevFormData?.loanInfoMeta?.annualPropertyTax ??
            mergedFormData.loanInfoMeta.annualPropertyTax,
          annualPropertyTaxAmount:
            prevFormData?.loanInfoMeta?.annualPropertyTaxAmount ??
            mergedFormData.loanInfoMeta.annualPropertyTaxAmount,
        },
        prepaidsMeta: {
          ...mergedFormData.prepaidsMeta,
          rate: finalFormData.prepaidsMeta.rate,
          monthsIns: finalFormData.prepaidsMeta.monthsIns,
          rateThou: finalFormData.prepaidsMeta.rateThou,
          // Same admin-configured-constant treatment as rate/monthsIns/
          // rateThou above (and interestRate1/termsInYears1 in loanInfoMeta
          // below) — always reflects the latest template on reload, same
          // tradeoff: a manual override here won't survive a reload either.
          daysInterest: finalFormData.prepaidsMeta.daysInterest,
          costs: finalFormData.prepaidsMeta.costs,
          costsAmount: finalFormData.prepaidsMeta.costsAmount,
        },
        paymentMeta: {
          ...mergedFormData.paymentMeta,
          monthlyExpense1: finalFormData.paymentMeta.monthlyExpense1,
          monthlyExpense2: finalFormData.paymentMeta.monthlyExpense2,
          monthlyExpense1Amount:
            finalFormData.paymentMeta.monthlyExpense1Amount,
          monthlyExpense2Amount:
            finalFormData.paymentMeta.monthlyExpense2Amount,
        },
      };

      computedFormData = withFreshCompanyTemplate;
      return withFreshCompanyTemplate;
    });

    // react-hook-form's reset() isn't a state setter React can batch/order
    // for us, so it's called separately, after the functional updater above
    // has synchronously computed the real final value.
    if (computedFormData) {
      reset(computedFormData);
    }
  }, [templateData, reset]);
};
